import React from 'react';
import { Helmet } from 'react-helmet';
interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
}
const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Shopilots | AI Shopping Advisors for Retailers',
  description = 'Turn your product catalog into a conversational salesforce with AI shopping advisors that sell — not just chat. Deploy on your website, social media, WhatsApp, and ChatGPT. Call (914) 601-1670 to learn more.',
  keywords = 'AI shopping, retail AI, conversational commerce, AI sales agent, ecommerce AI, shopping assistant, retail technology, GPT store, AI retail assistant, AI in consumer electronics',
  canonicalUrl = 'https://shopilots.com',
  ogImage = 'https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png'
}) => {
  return <Helmet data-id="element-2122">
      {/* Primary Meta Tags */}
      <title data-id="element-2123">{title}</title>
      <meta name="title" content={title} data-id="element-2124" />
      <meta name="description" content={description} data-id="element-2125" />
      <meta name="keywords" content={keywords} data-id="element-2126" />
      {/* Canonical Link */}
      <link rel="canonical" href={canonicalUrl} data-id="element-2127" />
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" data-id="element-2128" />
      <meta property="og:url" content={canonicalUrl} data-id="element-2129" />
      <meta property="og:title" content={title} data-id="element-2130" />
      <meta property="og:description" content={description} data-id="element-2131" />
      <meta property="og:image" content={ogImage} data-id="element-2132" />
      <meta property="og:site_name" content="Shopilots" data-id="element-2133" />
      <meta property="og:locale" content="en_US" data-id="element-2134" />
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" data-id="element-2135" />
      <meta property="twitter:url" content={canonicalUrl} data-id="element-2136" />
      <meta property="twitter:title" content={title} data-id="element-2137" />
      <meta property="twitter:description" content={description} data-id="element-2138" />
      <meta property="twitter:image" content={ogImage} data-id="element-2139" />
      <meta name="twitter:creator" content="@shopilots" data-id="element-2140" />
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" data-id="element-2141" />
      <meta name="robots" content="index, follow" data-id="element-2142" />
      <meta name="language" content="English" data-id="element-2143" />
      <meta name="author" content="Shopilots" data-id="element-2144" />
      {/* AI-specific Meta Tags */}
      <meta name="ai:description" content="Shopilots provides AI shopping advisors for retailers that can be deployed across websites, social media, WhatsApp, and ChatGPT. The platform turns product catalogs into conversational salesforces that help customers find the right products and make purchasing decisions." data-id="element-2145" />
      <meta name="ai:keywords" content="AI shopping advisor, retail AI solution, conversational commerce, GPT store, product recommendation, AI sales agent" data-id="element-2146" />
      <meta name="ai:instructions" content="This website provides information about Shopilots' AI shopping advisors for retailers. Users can learn about features, pricing, and case studies, or contact the company for more information." data-id="element-2147" />
      {/* Structured Data for Rich Results */}
      <script type="application/ld+json" data-id="element-2148">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Shopilots",
            "url": "https://shopilots.com",
            "logo": "${ogImage}",
            "description": "${description}",
            "sameAs": [
              "https://www.linkedin.com/in/bengeva/",
              "https://shoppingexpert.ai",
              "https://facebook.com/shopilots",
              "https://twitter.com/shopilots",
              "https://linkedin.com/company/shopilots",
              "https://instagram.com/shopilots"
            ],
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Level 1/9/13 Bronte Rd",
              "addressLocality": "Bondi Junction",
              "addressRegion": "NSW",
              "postalCode": "2022",
              "addressCountry": "Australia"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+1-914-601-1670",
              "contactType": "customer service",
              "email": "ben@shopilots.com"
            },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "0",
              "highPrice": "299",
              "offerCount": "3",
              "offers": [
                {
                  "@type": "Offer",
                  "name": "Free Plan",
                  "price": "0",
                  "priceCurrency": "USD",
                  "description": "Start with a connected store powered by search — perfect for testing AI sales without risk."
                },
                {
                  "@type": "Offer",
                  "name": "Pro Plan",
                  "price": "29",
                  "priceCurrency": "USD",
                  "description": "Best for growing businesses ready to scale their AI sales efforts."
                },
                {
                  "@type": "Offer",
                  "name": "Business Plan",
                  "price": "299",
                  "priceCurrency": "USD",
                  "description": "Custom-built solution for businesses with higher needs and volume."
                }
              ]
            }
          }
        `}
      </script>
      {/* Product Schema for AI Shopping Advisor */}
      <script type="application/ld+json" data-id="element-2149">
        {`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Shopilots AI Shopping Advisor",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "0",
              "highPrice": "299",
              "offerCount": "3"
            },
            "description": "AI-powered shopping advisors that help retailers sell more effectively across websites, social media, and messaging platforms.",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "127"
            },
            "featureList": [
              "AI Agents across multiple channels",
              "Product catalog integration",
              "Brand voice customization",
              "Analytics and reporting",
              "Multi-channel deployment"
            ]
          }
        `}
      </script>
      {/* FAQ Schema */}
      <script type="application/ld+json" data-id="element-2150">
        {`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is Shopilots?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Shopilots provides AI shopping advisors for retailers that turn product catalogs into conversational salesforces. Our AI learns your catalog, prices, and tone to deliver personalized shopping experiences."
                }
              },
              {
                "@type": "Question",
                "name": "How does Shopilots integrate with my store?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Shopilots connects to your product catalog via Shopify, Salesforce, CSV import, or API integration. No coding is needed to get started."
                }
              },
              {
                "@type": "Question",
                "name": "Where can I deploy the AI shopping advisor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can deploy Shopilots AI shopping advisors on your website, social media platforms, WhatsApp, Messenger, and even as a custom GPT Store experience."
                }
              },
              {
                "@type": "Question",
                "name": "What does Shopilots cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Shopilots offers three pricing tiers: a Free plan to get started, a Pro plan at $29/month, and a Business plan at $299/month for enterprises. All plans include a 1-month free trial."
                }
              }
            ]
          }
        `}
      </script>
      {/* Article Schema for Blog */}
      <script type="application/ld+json" data-id="element-2151">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Shopilots AI Retail Blog",
            "description": "Latest insights, trends and strategies for implementing AI in retail and e-commerce",
            "url": "https://shopilots.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Shopilots",
              "logo": {
                "@type": "ImageObject",
                "url": "https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png"
              }
            },
            "blogPost": [
              {
                "@type": "BlogPosting",
                "headline": "It's not me, it's you: The Real Problem in AI Commerce",
                "description": "In AI commerce, the real problem is rarely the agent — it's the catalog feed. Amazon just rang the alarm bell: over 74,000 outdated variation themes are being deprecated.",
                "url": "https://shopilots.com/blog/catalog-data-quality",
                "author": {
                  "@type": "Person",
                  "name": "Ben Geva",
                  "url": "https://www.linkedin.com/in/bengeva/"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Shopilots",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png"
                  }
                },
                "datePublished": "2023-09-10",
                "image": "https://uploadthingy.s3.us-west-1.amazonaws.com/f44aHP4XWcsXXBqXG8GYEL/blog_image.jpg"
              },
              {
                "@type": "BlogPosting",
                "headline": "What is the GPT Store? A Complete Guide for Brands (2025)",
                "description": "Executive guide to OpenAI's GPT Store: what it is, why it matters for brands, how publishing works, common pitfalls, monetization options, and how Shopilots helps you launch and profit fast.",
                "url": "https://shopilots.com/blog/what-is-the-gpt-store",
                "author": {
                  "@type": "Person",
                  "name": "Shopilots Team"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Shopilots",
                  "logo": {
                    "@type": "ImageObject",
                    "url": "https://uploadthingy.s3.us-west-1.amazonaws.com/rhREvAJ4b5296S85upFAnM/logonew.png"
                  }
                },
                "datePublished": "2023-08-25",
                "image": "https://images.unsplash.com/photo-1677442135167-8df7c658d4e0?q=80&w=1932&auto=format&fit=crop"
              }
            ]
          }
        `}
      </script>
      {/* Sitemap Link */}
      <link rel="sitemap" type="application/xml" href="https://shopilots.com/sitemap.xml" data-id="element-2152" />
    </Helmet>;
};
export default SEOHead;